import "./sidebar.scss";
import Tooltip from "../Tooltip";
import logo from "../../../assets/img/logo.svg";
import { useEffect, useRef, useState } from "react";
import CustomerAndGroups from "../../CustomerAndGroups";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { menuConfig } from "./menuConfig";

interface SidebarProps {
  onLogout: () => void;
}

const Sidebar = ({ onLogout }: SidebarProps) => {
  const permissions = useSelector(
    (state: any) => state.permissions.permissions,
  );
  const navigate = useNavigate();
  const { customerId } = useParams(); // Get current customerId from URL
  const sidebarRef = useRef<any>(null);
  const [drawerIsOpen, setDrawerIsOpen] = useState<boolean | null>(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(
    window.location.pathname === "/"
      ? "customerGroupsMenu"
      : window.location.pathname === "/modbus-client"
        ? "modbusClient"
        : window.location.pathname === "/data-backfill"
          ? "dataBackfill"
          : window.location.pathname === "/account-management"
            ? "apps"
            : "customerGroupsMenu",
  );

  const toggleDrawer = (flag: boolean | null, selectedMenu: string | null) => {
    setDrawerIsOpen(flag);
    setActiveMenu(selectedMenu);
  };

  useEffect(() => {
    const handleOutside = (e: any) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setDrawerIsOpen(null);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
    };
  }, [sidebarRef]);

  // Function to get the navigation URL for customer groups menu
  const getCustomerGroupsNavigationUrl = () => {
    if (customerId) {
      return `/${customerId}`;
    }
    return "/"; // Default to root if no customerId
  };

  useEffect(() => {
    setActiveMenu(
      window.location.pathname === "/"
        ? "customerGroupsMenu"
        : window.location.pathname === "/modbus-client"
          ? "modbusClient"
          : window.location.pathname === "/data-backfill"
            ? "dataBackfill"
            : window.location.pathname === "/account-management"
              ? "apps"
              : "customerGroupsMenu",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  return (
    <div ref={sidebarRef}>
      <div className="fixed left-0 bottom-0 top-0 flex flex-col items-start justify-between bg-darkcolor-600 py-[16px] 2xl:py-[24px] w-[55px] 2xl:w-[68px] min-w-[55px] 2xl:min-w-[68px] z-[60] sidebar">
        {/* Top Menu Items */}
        <div className="flex flex-col justify-center items-center w-full max-w-full sidebar-toplinks">
          <div className="flex flex-col justify-center items-center w-full max-w-full text-center px-[6px] pt-0 pb-[15px] 2xl:pb-[25px] sidebar-logo">
            <div>
              <img src={logo} alt="logo" className="object-contain" />
            </div>
          </div>

          <div className="flex flex-col justify-center items-center w-full max-w-full px-2 2xl:px-3 gap-1 sidebar-toplinks">
            {menuConfig
              .filter((menu: any) => menu.position === "top")
              .map((menu: any) => {
                if (
                  menu.permissionKey &&
                  !permissions?.[menu.permissionKey]?.[menu.canReadKey]
                ) {
                  return null; // Skip rendering if permission is not granted
                }

                return (
                  <div
                    key={menu.id}
                    data-testid={`${menu.id}Icon`}
                    className={`px-[12px] pt-[10px] pb-[5px] rounded-[6px] cursor-pointer flex items-center justify-center hover:bg-primary-700 border-solid border-2 border-transparent hover:border-primary-500 iconbtn ${
                      activeMenu === menu.id ? "activeMenu" : ""
                    }`}
                    onClick={() => {
                      toggleDrawer(menu.toggleDrawer, menu.id);
                      if (menu.navigateTo) {
                        // Special handling for customerGroupsMenu
                        if (menu.id === "customerGroupsMenu") {
                          navigate(getCustomerGroupsNavigationUrl(), {
                            replace: true,
                          });
                        } else {
                          navigate(menu.navigateTo, { replace: true });
                        }
                      }
                    }}
                  >
                    <Tooltip content={menu.tooltip} direction="right">
                      {menu.icon}
                    </Tooltip>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Bottom Menu Items */}
        <div className="flex flex-col justify-center w-full max-w-full px-2 2xl:px-3 gap-2 sidebar-bottomlinks">
          {menuConfig
            .filter((menu: any) => menu.position === "bottom")
            .map((menu: any) => (
              <div
                key={menu.id}
                data-testid={`${menu.id}Icon`}
                className={`p-1 2xl:p-2 rounded-md cursor-pointer text-center hover:bg-primary-700 border-solid border-2 border-transparent hover:border-primary-500 iconbtn ${
                  activeMenu === menu.id ? "activeMenu" : ""
                }`}
                onClick={() => {
                  if (menu.action === "logout") {
                    onLogout(); // Trigger logout action
                  } else {
                    toggleDrawer(menu.toggleDrawer, menu.id);
                    if (menu.navigateTo) {
                      navigate(menu.navigateTo, { replace: true });
                    }
                  }
                }}
              >
                <Tooltip content={menu.tooltip} direction="right">
                  {menu.icon}
                </Tooltip>
              </div>
            ))}
        </div>
      </div>
      {drawerIsOpen && <CustomerAndGroups toggleSidebar={toggleDrawer} />}
    </div>
  );
};

export default Sidebar;
